import React, { useEffect, useState } from 'react'
import { Flex, Text } from '@chakra-ui/core'
import axios from 'axios'
import Auth from 'services/authService'
import endpoints from 'constants/endpoints'
import keys from 'constants/keys'
import { LoginWrapper } from 'components/Login/LoginWrapper'

// Entry point for a Storee-internal admin tool: it opens this route in a new tab as
// `/admin/login-as#code=<exchange_code>` to log in as a given user without ever handling their
// password. The code lives in the URL hash (never a query string) so it's never sent to or logged
// by any server on its way here, and it's single-use — redeemed for an id_token (+ refresh_token,
// if the mint flow returned one) exactly once.
//
// Still no access_token, so this can't build a real Amplify/Cognito session
// (handleAuthenticatedUserManually requires all three) — the refresh_token, when present, is used
// via a raw Cognito REFRESH_TOKEN_AUTH call instead (see authService.ts's refreshViewAsIdToken).
// Closing the tab or explicitly logging out ends the view-as session (see authService.ts).
//
// Deliberately does NOT dispatch setToken/setUserId/setIsLoggedIn/handleConfig itself, and does
// NOT wait on redux-persist before reloading — persist:auth is shared, unlocked localStorage
// across every open Storee tab on this origin, so racing another tab's write there is a real
// failure mode (see the multi-tab notes in App.tsx). This page's only job is putting the
// credential in sessionStorage (tab-exclusive, immune to that race) and reloading; App.tsx's
// boot effect is what actually establishes the identity, re-asserted fresh from sessionStorage on
// every load rather than trusted from persisted state.
//
// This route intentionally has no RequireAuth — the whole point is to establish a session from
// nothing.
const AdminLoginAs: React.FC = () => {
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const redeem = async () => {
      const hash = window.location.hash
      const code = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash).get('code')

      // Scrub the code out of the visible URL/history right away — defense in depth, since the
      // code is already single-use server-side. No retry-with-same-code logic follows this.
      window.history.replaceState(null, '', window.location.pathname)

      if (!code) {
        setError('This login link is missing its code — ask the admin to generate a new one.')
        return
      }

      try {
        // The backend's request framework requires a client_id header on every call,
        // auth or not (separate from the Authorization token) — same convention the
        // existing OTP endpoints (getOTP/loginOTP in redux/actions/auth.ts) already
        // follow, even though this handler doesn't otherwise use the value itself.
        const response = await axios.get(endpoints.viewAsRedeem(code), {
          headers: { client_id: process.env.REACT_APP_COGNITO_USER_POOL_WEB_CLIENT_ID || '' },
        })
        const idToken = response?.data?.id_token
        const refreshToken = response?.data?.refresh_token
        if (!idToken) {
          setError('This login link has expired or was already used — ask the admin to generate a new one.')
          return
        }

        Auth.setViewAsIdToken(idToken, refreshToken)

        // Hard navigation, not react-router's navigate() — this is a brand-new identity, and a
        // full reload guarantees every module/component boots fresh against it instead of
        // carrying over anything already initialized in this page's JS context. App.tsx's boot
        // effect reads the sessionStorage credential this just set and establishes isLoggedIn/uid
        // /token/config from it directly.
        window.location.href = keys.ROUTE_NAMES.NEWS_FEED
      } catch (err) {
        setError('This login link has expired or was already used — ask the admin to generate a new one.')
      }
    }

    redeem()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <LoginWrapper>
      <Flex flexDirection="column" alignItems="center" justifyContent="center" style={{ rowGap: 12 }} p="20px">
        {error ? (
          <Text textAlign="center" fontSize="15px" color="#c62828">
            {error}
          </Text>
        ) : (
          <Text textAlign="center" fontSize="15px" color="#666">
            Signing you in…
          </Text>
        )}
      </Flex>
    </LoginWrapper>
  )
}

export default AdminLoginAs
